/**
 * EngineDetailKeyword Component
 *
 * Engine-specific detail view for Keyword Engine.
 * Displays keyword opportunities, existing rankings, and competitor analysis
 * using shared sub-components from KeywordEngineResultsDisplay.
 */

import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import type { KeywordEngineSpecificAnalysis } from "@a7y/types";
import { Subheading, Text } from "@a7y/tailwind-catalyst";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../tooltip";
import {
  StatCard,
  CollapsibleSection as KeywordCollapsibleSection,
  ScoreBadge,
  RankBadge,
  CompetitorRanks,
  buildCompetitorMetadataMap,
} from "@/components/audit/keyword/KeywordEngineResultsDisplay";
import { CollapsibleSection } from "./CollapsibleSection";

/** Column definition for the keyword table */
interface KeywordTableColumn<T> {
  key: string;
  label: string;
  align: "left" | "right";
  tooltip?: { text: string; href: string };
  render: (kw: T) => ReactNode;
}

/** Keyword shape accepted by KeywordTable (union of suggested & existing fields) */
type KeywordTableItem = {
  term: string;
  volume: number;
  opportunity: number;
  relevance: number;
  difficulty: number;
  rank?: number;
  competitors?: Array<{ id: string; rank: number | null }>;
};

interface KeywordTableProps<T extends KeywordTableItem> {
  keywords: T[];
  columns: KeywordTableColumn<T>[];
}

const TH_CLASS =
  "px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider";

const GLOSSARY_BASE = "https://www.apptonomy.ai/docs/glossary";

const COLUMN_TOOLTIPS = {
  volume: { text: "A 0\u2013100 score estimating monthly search traffic for this keyword, using a logarithmic scale.", href: `${GLOSSARY_BASE}/#keyword-volume` },
  opportunity: { text: "A 0\u2013100 composite score combining volume, difficulty, and trend data to surface the most promising keywords.", href: `${GLOSSARY_BASE}/#keyword-opportunity` },
  relevance: { text: "A 1\u2013100 AI-generated score measuring how closely a keyword relates to what your app actually does.", href: `${GLOSSARY_BASE}/#keyword-relevance` },
  difficulty: { text: "A 0\u2013100 score measuring how competitively advertisers bid on this keyword.", href: `${GLOSSARY_BASE}/#keyword-difficulty` },
  competitors: { text: "The search-result positions that each analysed competitor holds for this keyword.", href: `${GLOSSARY_BASE}/#competitor-ranks` },
} as const;

/** Reusable keyword data table used by both Suggested and Existing keyword sections. */
function KeywordTable<T extends KeywordTableItem>({ keywords, columns }: KeywordTableProps<T>) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
        <thead className="bg-gray-50 dark:bg-zinc-800">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${TH_CLASS} ${col.align === "right" ? "text-right" : "text-left"}`}
              >
                <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                  {col.label}
                  {col.tooltip && (
                    <Tooltip delayDuration={250}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
                        >
                          <InformationCircleIcon className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs normal-case text-left">
                        <p>{col.tooltip.text}</p>
                        <a
                          href={col.tooltip.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-1.5 underline text-blue-300 hover:text-blue-200"
                        >
                          Learn more
                        </a>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
          {keywords.map((kw) => (
            <tr key={kw.term} className="hover:bg-gray-50 dark:hover:bg-zinc-800">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-4 py-2 whitespace-nowrap text-sm ${col.align === "right" ? "text-right" : ""}`}
                >
                  {col.render(kw)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface EngineDetailKeywordProps {
  /** Keyword-specific analysis data */
  analysis: KeywordEngineSpecificAnalysis;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function EngineDetailKeyword({ analysis, printMode, className = "" }: EngineDetailKeywordProps) {
  const [statsOpen, setStatsOpen] = useState(true);
  const { suggested_keywords, existing_keywords, competitors } = analysis;

  const competitorMetadata = useMemo(() => buildCompetitorMetadataMap(competitors), [competitors]);

  const competitorKeywordsCount = competitors.reduce(
    (total, comp) => total + comp.keywords.length,
    0
  );

  // Sort suggested by opportunity descending
  const sortedSuggested = [...suggested_keywords].sort((a, b) => b.opportunity - a.opportunity);

  // Sort existing by rank ascending
  const sortedExisting = [...existing_keywords].sort((a, b) => a.rank - b.rank);

  type SuggestedKw = KeywordEngineSpecificAnalysis["suggested_keywords"][number];
  type ExistingKw = KeywordEngineSpecificAnalysis["existing_keywords"][number];

  const suggestedColumns = useMemo(
    (): KeywordTableColumn<SuggestedKw>[] => [
      { key: "term", label: "Keyword", align: "left", render: (kw) => <span className="font-medium text-gray-900 dark:text-white">{kw.term}</span> },
      { key: "volume", label: "Volume", align: "right", tooltip: COLUMN_TOOLTIPS.volume, render: (kw) => <span className="text-gray-600 dark:text-gray-400">{kw.volume}</span> },
      { key: "opportunity", label: "Opportunity", align: "right", tooltip: COLUMN_TOOLTIPS.opportunity, render: (kw) => <ScoreBadge value={kw.opportunity} type="opportunity" /> },
      { key: "relevance", label: "Relevance", align: "right", tooltip: COLUMN_TOOLTIPS.relevance, render: (kw) => <ScoreBadge value={kw.relevance} type="relevance" /> },
      { key: "difficulty", label: "Difficulty", align: "right", tooltip: COLUMN_TOOLTIPS.difficulty, render: (kw) => <ScoreBadge value={kw.difficulty} type="difficulty" /> },
      { key: "competitors", label: "Competitor Ranks", align: "left", tooltip: COLUMN_TOOLTIPS.competitors, render: (kw) => <CompetitorRanks competitors={kw.competitors} competitorMetadata={competitorMetadata} /> },
    ],
    [competitorMetadata],
  );

  const existingColumns = useMemo(
    (): KeywordTableColumn<ExistingKw>[] => [
      { key: "term", label: "Keyword", align: "left", render: (kw) => <span className="font-medium text-gray-900 dark:text-white">{kw.term}</span> },
      { key: "rank", label: "Rank", align: "right", render: (kw) => <RankBadge rank={kw.rank} /> },
      { key: "volume", label: "Volume", align: "right", tooltip: COLUMN_TOOLTIPS.volume, render: (kw) => <span className="text-gray-600 dark:text-gray-400">{kw.volume}</span> },
      { key: "opportunity", label: "Opportunity", align: "right", tooltip: COLUMN_TOOLTIPS.opportunity, render: (kw) => <ScoreBadge value={kw.opportunity} type="opportunity" /> },
      { key: "relevance", label: "Relevance", align: "right", tooltip: COLUMN_TOOLTIPS.relevance, render: (kw) => <ScoreBadge value={kw.relevance} type="relevance" /> },
      { key: "difficulty", label: "Difficulty", align: "right", tooltip: COLUMN_TOOLTIPS.difficulty, render: (kw) => <ScoreBadge value={kw.difficulty} type="difficulty" /> },
    ],
    [],
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Summary Stats */}
      <CollapsibleSection
        title="Summary Statistics"
        isExpanded={statsOpen}
        onToggle={() => setStatsOpen((v) => !v)}
        headerColor="gray"
        forceExpanded={printMode}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Suggested Keywords"
            value={suggested_keywords.length}
            description="Keywords you don't rank for"
            color="green"
          />
          <StatCard
            label="Existing Keywords"
            value={existing_keywords.length}
            description="Keywords you currently rank for"
            color="blue"
          />
          <StatCard
            label="Competitors Analyzed"
            value={competitors.length}
            description="Competitor apps in analysis"
            color="purple"
          />
          <StatCard
            label="Competitor Keywords"
            value={competitorKeywordsCount}
            description="Total keywords from competitors"
            color="amber"
          />
        </div>
      </CollapsibleSection>

      {/* Suggested Keywords */}
      {sortedSuggested.length > 0 && (
        <KeywordCollapsibleSection title={`Suggested Keywords (${sortedSuggested.length})`} defaultExpanded forceExpanded={printMode}>
          <KeywordTable
            keywords={sortedSuggested}
            columns={suggestedColumns}
          />
        </KeywordCollapsibleSection>
      )}

      {/* Existing Keywords */}
      {sortedExisting.length > 0 && (
        <KeywordCollapsibleSection title={`Existing Keywords (${sortedExisting.length})`} defaultExpanded forceExpanded={printMode}>
          <KeywordTable
            keywords={sortedExisting}
            columns={existingColumns}
          />
        </KeywordCollapsibleSection>
      )}

      {/* Competitor Analysis */}
      {competitors.length > 0 && (
        <KeywordCollapsibleSection title={`Competitor Analysis (${competitors.length})`} defaultExpanded forceExpanded={printMode}>
          <div className="space-y-4">
            {competitors.map((comp) => (
              <div
                key={comp.id}
                className="border border-gray-200 dark:border-zinc-700 rounded-lg p-4"
              >
                <div className="flex items-center gap-3 mb-3">
                  {comp.icon_url_cached && (
                    <img
                      src={comp.icon_url_cached}
                      alt={comp.app_name}
                      className="w-10 h-10 rounded-lg shadow-sm bg-transparent"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <Subheading level={4} className="truncate">
                      {comp.app_name}
                    </Subheading>
                    {comp.publisher_name && (
                      <Text className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {comp.publisher_name}
                      </Text>
                    )}
                  </div>
                  <Text className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {comp.keywords.length} keywords
                  </Text>
                </div>

                {comp.keywords.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700 text-sm">
                      <thead>
                        <tr>
                          <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                            Keyword
                          </th>
                          <th className="px-3 py-1 text-right text-xs font-medium text-gray-500 uppercase">
                            Rank
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {comp.keywords.slice(0, 10).map((kw) => (
                          <tr key={kw.term}>
                            <td className="px-3 py-1 text-gray-900 dark:text-white">{kw.term}</td>
                            <td className="px-3 py-1 text-right">
                              <RankBadge rank={kw.rank} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {comp.keywords.length > 10 && (
                      <Text className="text-xs text-gray-500 mt-2">
                        Showing first 10 of {comp.keywords.length} keywords
                      </Text>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </KeywordCollapsibleSection>
      )}
    </div>
  );
}
